import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, Spin, Alert, Typography, Button } from 'antd';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { getDocumentoVigente } from '../../services/legalService';
import { getErrorMessage } from '../../utils/errorHandler';

const { Title, Text } = Typography;

const tableWrapStyle = {
  width: '100%',
  overflowX: 'auto',
  margin: '12px 0 20px',
};

const tableStyle = {
  width: '100%',
  borderCollapse: 'collapse',
  fontSize: 14,
};

const cellStyle = {
  border: '1px solid #e8e8e8',
  padding: '8px 12px',
  textAlign: 'left',
  verticalAlign: 'top',
};

const thStyle = {
  ...cellStyle,
  background: 'var(--color-surface-muted, #F8F4EC)',
  fontWeight: 600,
};

const markdownComponents = {
  h1: ({ children }) => <Title level={3} style={{ marginTop: 0 }}>{children}</Title>,
  h2: ({ children }) => <Title level={5} style={{ marginTop: 16 }}>{children}</Title>,
  h3: ({ children }) => <Title level={5} style={{ marginTop: 12 }}>{children}</Title>,
  p: ({ children }) => (
    <Text style={{ display: 'block', marginBottom: 8 }}>{children}</Text>
  ),
  strong: ({ children }) => <Text strong>{children}</Text>,
  ul: ({ children }) => (
    <ul style={{ margin: '8px 0 16px', paddingLeft: 24 }}>{children}</ul>
  ),
  ol: ({ children }) => (
    <ol style={{ margin: '8px 0 16px', paddingLeft: 24 }}>{children}</ol>
  ),
  li: ({ children }) => <li style={{ marginBottom: 4 }}>{children}</li>,
  a: ({ href, children }) => (
    <a href={href} target="_blank" rel="noopener noreferrer">{children}</a>
  ),
  table: ({ children }) => (
    <div style={tableWrapStyle}>
      <table style={tableStyle}>{children}</table>
    </div>
  ),
  thead: ({ children }) => <thead>{children}</thead>,
  tbody: ({ children }) => <tbody>{children}</tbody>,
  tr: ({ children }) => <tr>{children}</tr>,
  th: ({ children }) => <th style={thStyle}>{children}</th>,
  td: ({ children }) => <td style={cellStyle}>{children}</td>,
};

export default function DocumentoLegalPage({ tipo, tituloPagina, voltarPara = '/login' }) {
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await getDocumentoVigente(tipo);
        if (!cancelled) setDoc(data);
      } catch (err) {
        if (!cancelled) setError(getErrorMessage(err, 'Não foi possível carregar o documento.'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [tipo]);

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      overflowY: 'auto',
      WebkitOverflowScrolling: 'touch',
      background: 'var(--color-background)',
      padding: '24px 16px 48px',
    }}>
      <Card style={{ maxWidth: 720, margin: '0 auto' }}>
        <div style={{ marginBottom: 16 }}>
          <Link to={voltarPara}><Button type="link" style={{ padding: 0 }}>Voltar</Button></Link>
        </div>
        {loading && <Spin tip="Carregando..." />}
        {error && <Alert type="error" message={error} showIcon />}
        {doc && (
          <>
            <Title level={4}>{tituloPagina || doc.titulo}</Title>
            <Text type="secondary" style={{ display: 'block', marginBottom: 24 }}>
              Versão {doc.versao}
            </Text>
            <div style={{ lineHeight: 1.6 }}>
              <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
                {doc.conteudo || ''}
              </ReactMarkdown>
            </div>
          </>
        )}
      </Card>
    </div>
  );
}
